import { useState, useEffect, FC } from 'react';
import { deleteRequest, getRequest, postRequest } from '../utils/api';
import { Link } from 'react-router-dom';
import { System } from '../interfaces/system';
import DropdownModal from '../components/DropdownModal';
import config from '../config';
import { formatDate } from '../utils/datetime';

const Systems: FC = () => {
  const [systems, setSystems] = useState<System[]>([]);
  const [newSystem, setNewSystem] = useState<System | null>(null);
  const [name, setName] = useState('');
  const [checkPeriod, setCheckPeriod] = useState<number>(0);
  const [dependencyId, setDependencyId] = useState<number | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [checkInUrl, setCheckInUrl] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchSystems();
  }, []);

  const fetchSystems = async () => {
    try {
      setErrorMessage(null);
      const data = await getRequest<System[]>('/systems');
      setSystems(data);
    } catch (error: any) {
      console.error('Error fetching systems:', error);
      setErrorMessage(error.message);
    }
  };

  const addSystem = async () => {
    try {
      setErrorMessage(null);
      const result = await postRequest('/systems', {
        name,
        check_period: checkPeriod,
        // dependency_id: dependencyId, // NOT FULLY IMPLEMENTED YET
      });
      setNewSystem(result);
      setCheckInUrl(
        config.apiBaseUrl + 'integrations/' + result.id + '/checkIn',
      );
      setName('');
      setCheckPeriod(0);
      setDependencyId(null);
      // setIsModalOpen(false);
      fetchSystems();
    } catch (error: any) {
      setErrorMessage(error.message);
      console.error('Error adding system:', error);
    } finally {
      setLoading(false);
    }
  };

  const deleteSystem = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this system?')) {
      return;
    }
    try {
      setErrorMessage(null);
      await deleteRequest(`/systems/${id}`);
      fetchSystems();
    } catch (error: any) {
      setErrorMessage(error.message);
      console.error('Error deleting system:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleEnabled = async (id: number, enabled: boolean) => {
    try {
      setErrorMessage(null);
      await postRequest(`/systems/${id}/${enabled ? 'disable' : 'enable'}`);
      fetchSystems();
    } catch (error: any) {
      setErrorMessage(error.message);
      console.error('Error toggling system:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <h2 className="mb-3">Systems</h2>

      {errorMessage && (
        <div className="alert alert-danger" role="alert">
          {errorMessage}
        </div>
      )}

      {checkInUrl && (
        <div className="alert alert-info" role="alert">
          <p>
            <strong>API Check-In URL:</strong> {checkInUrl}
          </p>
          <p>
            You will need to authenticate with the system's token. Add the
            following header to your request:
          </p>
          <p>
            <code className="auth-code">
              Authorization: Bearer {newSystem?.token}
            </code>
          </p>
        </div>
      )}

      {!isModalOpen ? (
        <button
          className="btn btn-primary"
          onClick={() => setIsModalOpen(true)}
        >
          Add System
        </button>
      ) : (
        <DropdownModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          title="Add System"
        >
          <form
            onSubmit={(e) => {
              e.preventDefault();
              addSystem();
            }}
          >
            <div className="mb-3">
              <label htmlFor="name" className="form-label">
                Name
              </label>
              <input
                type="text"
                id="name"
                className="form-control"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </div>
            <div className="mb-3">
              <label htmlFor="checkPeriod" className="form-label">
                Check Period (seconds)
              </label>
              <input
                type="number"
                id="checkPeriod"
                className="form-control"
                value={checkPeriod}
                onChange={(e) => setCheckPeriod(Number(e.target.value))}
                required
              />
            </div>
            {/* NOT FULLY IMPLEMENTED YET */}
            {/*<div className="mb-3">*/}
            {/*  <label htmlFor="dependencyId" className="form-label">*/}
            {/*    System Dependency (optional)*/}
            {/*  </label>*/}
            {/*  <input*/}
            {/*    type="number"*/}
            {/*    id="dependencyId"*/}
            {/*    className="form-control"*/}
            {/*    value={dependencyId || ''}*/}
            {/*    onChange={(e) => setDependencyId(Number(e.target.value))}*/}
            {/*  />*/}
            {/*</div>*/}
            <div className="d-flex justify-content-between">
              <button
                type="submit"
                className="btn btn-success"
                disabled={loading}
              >
                Add System
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setIsModalOpen(false)}
              >
                Cancel
              </button>
            </div>
          </form>
        </DropdownModal>
      )}
      <h3 className="mt-4 mb-3">Existing Systems</h3>
      <table className="table table-striped table-bordered">
        <thead className="table-dark">
          <tr>
            <th>ID</th>
            <th>Name</th>
            <th>Check Period</th>
            <th>Health Status</th>
            <th>Last System Checkin</th>
            {/*<th>Dependency</th>*/}
            <th>Enabled</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {systems.map((system) => (
            <tr key={system.id}>
              <td>{system.id}</td>
              <td>
                <Link to={`/systems/${system.id}`}>{system.name}</Link>
              </td>
              <td>{system.check_period}</td>
              <td>{system.last_health_status}</td>
              <td>{formatDate(system.last_health_check)}</td>
              {/*<td>{system.dependency?.name || '-'}</td>*/}
              <td>{system.enabled ? 'Yes' : 'No'}</td>
              <td>
                <button
                  onClick={() => toggleEnabled(system.id, system.enabled)}
                  className="btn btn-primary"
                  disabled={loading}
                >
                  {system.enabled ? 'Disable' : 'Enable'}
                </button>
                <button
                  className="btn btn-danger"
                  onClick={() => deleteSystem(system.id)}
                  disabled={loading}
                >
                  Delete
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default Systems;
